import { Button, Intent, RadioGroup, Radio } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FormikProps } from "formik";
import {
  FGCustomInput,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import styled from "styled-components";
import * as Yup from "yup";

import {
  DogApi,
  DysplasiaRequestApi,
  DysplasiaRequestDto,
  ERequestState,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../api";
import {
  ButtonsBloc,
  CancelButton,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  FGSRSHMultiCheckboxInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useDialog, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DysplasiaRequestValueResult } from "./DysplasiaRequestValueResult";
import { DysplasiaResultsRemarksList } from "./DysplasiaResultsRemarksList";

export interface IDysplasiaRequestDetailProps {
  dysplasiaRequestId: number;
  editMode: boolean;
  requestInfo?: RequestInfoDto;
}

const StyledEvaluation = styled.div`
  margin-left: 30px;
`;

export const DysplasiaRequestDetail: React.FunctionComponent<IDysplasiaRequestDetailProps> = ({
  dysplasiaRequestId,
  editMode,
  requestInfo,
}) => {
  const { masterRequestId = "0" } = useParams<{
    masterRequestId: string;
  }>();
  const api = useApiService(DysplasiaRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  const [isAppealWithRequest, setisAppealWithRequest] = React.useState(0);

  /* ========== Hooks and needed variables ===========*/
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const history = useHistory();
  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();
  const formikRef = React.useRef<FormikProps<DysplasiaRequestDto>>();
  const [vetIdRequired, setVetIdRequired] = React.useState(true);
  const [dogId, setDogId] = React.useState<number>(0);
  const [clinicId, setClinicId] = React.useState<number>();
  const [clinicIdRequired, setClinicIdRequired] = React.useState(true);
  const [minTypesRequired, setMinTypesRequired] = React.useState<number[]>();
  const [appealDysplasiaRequestId, setAppealDysplasiaRequestId] = React.useState<number>();
  const [isAppeal, setIsAppeal] = React.useState(false);
  /* ========== Referentials ===========*/
  const [dysplasiaRequests, drloading] = useReferential(
    (a) =>
      (+masterRequestId > 0 || requestInfo?.masterRequestId > 0) && dogId
        ? a.getOwnDysplasiaRequests(dogId)
        : a.getDysplasiaRequests(),
    true,
    [dogId]
  );
  const [positions, posloading] = useReferential((a) => a.getDysplasiaPositions(currentLanguage.languageId), false);
  const [vets, vloading] = useReferential((a) => a.getVets(), true, [clinicId]);
  const [vetsByClinic, vcloading] = useReferential((a) => a.getVetsByClinicId(clinicId), true, [clinicId]);
  const [clinics, cloading] = useReferential((a) => a.getClinics(), true);
  const [commissionTypes, ctloading] = useReferential((a) => a.getCommissionTypes(), false);
  const [dysplasiaTypes, dtloading] = useReferential((a) => a.getDysplasiaTypes(true, 0), true, [
    appealDysplasiaRequestId,
  ]);
  /* ========== Getting Data ===========*/

  const getApiFn = React.useCallback(
    async (id = dysplasiaRequestId) => {
      if (id > 0) {
        const d = await api.get(id);
        return d;
      } else if (+masterRequestId > 0) {
        const d = await api.getSubRequest(+masterRequestId, currentLanguage.languageId);
        d.radioDate = new Date();
        return d;
      } else {
        return new DysplasiaRequestDto({
          request: new RequestDto({
            requestDate: new Date(),
            status: ERequestState.REQUESTED,
            remark: "",
          }),
          typeIdsChecked: [],
        }).toJSON();
      }
    },
    [api, currentLanguage.languageId, dysplasiaRequestId, masterRequestId]
  );

  const { data, loading, saving, saveItem, refresh } = useCrudApi<DysplasiaRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = dysplasiaRequestId) => getApiFn(id),
        saveApiFn: (d: DysplasiaRequestDto) => api.save(d),
        onSavedRoute: (d: DysplasiaRequestDto) => `${ERoutes.dysplasiaRequest}/${d.dysplasiaRequestId}/detail/edit`,
      }),
      [dysplasiaRequestId, getApiFn, api]
    ),
    [dysplasiaRequestId]
  );

  React.useEffect(() => {
    if (!loading) {
      setisAppealWithRequest(!!data?.appealRequestId ? 1 : 0);
    }
  }, [data?.appealRequestId, loading]);

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (dysplasiaRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [dysplasiaRequestId, personApi, saveItem, showDialogPromise, t]
  );

  /* ========== Yup ===========*/
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape(
      {
        dogId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("dog-availability", t(ETLCodes.DogHasAnotherRequestError), async function (value) {
            if (+masterRequestId === 0) {
              const res = await api.isDogAvailable(value, dysplasiaRequestId);
              return !res.value;
            }
            return true;
          })
          .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
            if (+masterRequestId === 0) {
              if (dysplasiaRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                const res = await dogApi.isDogDeceased(value);
                return !res.value;
              }
            }
            return true;
          })
          .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
            if (+masterRequestId === 0) {
              if (dysplasiaRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                const result = await dogApi.isDogExcluded(value);
                return !result.value;
              }
            }
            return true;
          }),
        commissionType: Yup.string()
          .nullable()
          .test("isAppealTest", t(ETLCodes.Required), function (value) {
            return !(!value && !isAppeal);
          }),
        appealRequestId: Yup.number()
          .nullable()
          .test("appeal-required", t(ETLCodes.Required), async function (value) {
            if (isAppeal && !!isAppealWithRequest) return !!value;
            return true;
          }),
        typeIdsChecked: Yup.array()
          .of(Yup.number())
          .min(1, t(ETLCodes.AtLeastOneValueMustBeSelected))
          .test("isAppeal", t(ETLCodes.AtLeastOneAppealMustBeTicked), async function (value: number[]) {
            if (!this.parent.isAppeal) {
              return true;
            } else {
              return minTypesRequired ? value.some((e) => minTypesRequired.includes(e)) : true;
            }
          }),
        clinicId: Yup.string()
          .nullable()
          .when("vetId", (value, schema) =>
            value === undefined || value === null ? schema.required(t(ETLCodes.Required)) : schema
          ),
        vetId: Yup.string()
          .nullable()
          .when("clinicId", (value, schema) =>
            value === undefined || value === null ? schema.required(t(ETLCodes.Required)) : schema
          ),
        radioDate: Yup.date().max(new Date(), t(ETLCodes.MustBeInThePast)).nullable().required(t(ETLCodes.Required)),
        request: Yup.object().shape({
          personId: Yup.number()
            .nullable()
            .required(t(ETLCodes.Required))
            .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
              if (+masterRequestId === 0) {
                if (dysplasiaRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                  if (value !== 0 && value !== null && value !== undefined) {
                    const result = await personApi.missingAddressInformationCheck(value);
                    return result.value;
                  }
                  return true;
                }
              }
              return true;
            })
            .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
              console.log(" first master request id ", +masterRequestId);
              if (+masterRequestId === 0) {
                if (dysplasiaRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                  console.log("person id checked is ", value);
                  const result = await personApi.isDeceased(value);
                  return !result.value;
                }
              }
              return true;
            })
            .test("is-owner", t(ETLCodes.IsNotBelgianOwnerError), async function (value) {
              if (+masterRequestId === 0) {
                const res = await dogApi.isBelgianDogOwner(dogId, value);
                return res.value;
              }
              return true;
            }),
          requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
        }),
      },
      [["clinicId", "vetId"]]
    );
  }, [
    api,
    data?.request?.status,
    dogApi,
    dogId,
    dysplasiaRequestId,
    isAppeal,
    isAppealWithRequest,
    masterRequestId,
    minTypesRequired,
    personApi,
    t,
  ]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  /* ========== Variables used for display rules ===========*/
  const editable = React.useMemo(
    () =>
      data?.request?.status === ERequestState.REQUESTED &&
      (requestInfo?.masterRequestStateCode === ERequestState.REQUESTED || requestInfo?.masterRequestStateCode == null),
    [data, requestInfo]
  );

  const blocked = React.useMemo(
    () =>
      requestInfo?.isRequestBlocked ||
      requestInfo?.isMasterRequestBlocked ||
      requestInfo?.requestStateCode === ERequestState.EXECUTED ||
      (requestInfo?.masterRequestStateCode != null &&
        !(requestInfo?.masterRequestStateCode === ERequestState.REQUESTED)),
    [requestInfo]
  );
  const onCancel = React.useCallback(() => history.push(`${ERoutes.dysplasiaRequest}`), [history]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);
  const openPersonFromVet = React.useCallback(
    async (vetId: number) => {
      var personId = await personApi.getByVetId(vetId);
      openNewTab(`${ERoutes.people}/${personId}/detail/view`);
    },
    [openNewTab, personApi]
  );

  const [certificateRegenLoading, setcertificateRegenLoading] = React.useState(false);
  const regenerateDysplasiaCertificate = React.useCallback(async () => {
    setcertificateRegenLoading(true);
    await api.regenerateDysplasiaCertificate(requestInfo?.parentRequestId);
    setcertificateRegenLoading(false);
  }, [api, requestInfo?.parentRequestId]);

  return (
    <SmallFormGenerator
      initialValues={data}
      editMode={editMode}
      onSubmit={saveCheck}
      validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
      loading={loading}
      saving={saving || saveChecking}
      onCancel={onCancel}
      hideButtons={data?.request?.status === ERequestState.EXECUTED}
      formikInnerRef={(f) => (formikRef.current = f)}
    >
      <FGListen
        field="isAppeal"
        onChanged={(value, formik) => {
          setIsAppeal(value);
          if (!value) {
            setAppealDysplasiaRequestId(0);
            formik.setFieldValue("appealRequestId", null);
          }
        }}
      />
      <FGListen
        field="dogId"
        onChanged={(value) => {
          console.log("set dog Id");
          setDogId(value);
        }}
      />
      <FGListen
        field="appealRequestId"
        onChanged={async (value, formik) => {
          if (value && value !== data.appealRequestId) {
            const appealRequest = await api.getAppealRequest(value);
            setAppealDysplasiaRequestId(appealRequest.dysplasiaRequestId);
            setMinTypesRequired(appealRequest?.typeIdsChecked);
            formik.setFieldValue("request.personId", appealRequest.request.personId);
            formik.setFieldValue("dogId", appealRequest.dogId);
            formik.setFieldValue("typeIdsChecked", appealRequest.typeIdsChecked);
            formik.setFieldValue("radioDate", appealRequest.radioDate);
            formik.setFieldValue("position", appealRequest.position);
            formik.setFieldValue("clinicId", appealRequest.clinicId);
            formik.setFieldValue("vetId", appealRequest.vetId);
          }
        }}
      />
      <FGListen
        field="vetId"
        onChanged={(value) => {
          if (value) setClinicIdRequired(false);
          else setClinicIdRequired(true);
        }}
      />
      <FGListen
        field="clinicId"
        onChanged={(value) => {
          setClinicId(value);
          if (value) setVetIdRequired(false);
          else setVetIdRequired(true);
        }}
      />
      <FieldSet
        title={t(ETLCodes.RequestData)}
        rightElement={
          <>
            {dysplasiaRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            )}
            {requestInfo && requestInfo?.requestStateCode === ERequestState.EXECUTED && (
              <Button
                text={t(ETLCodes.RegenDysplasiaCertificate)}
                onClick={regenerateDysplasiaCertificate}
                icon={IconNames.UPLOAD}
                intent={Intent.PRIMARY}
                loading={certificateRegenLoading}
              />
            )}
          </>
        }
      >
        <FieldGroup columns={2}>
          <FGSRSHDateMaskInput
            name="request.requestDate"
            label={t(ETLCodes.RequestDate)}
            requiredMark
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
          />
          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
          />
          <FGTextAreaInput
            name="remark"
            label={t(ETLCodes.RemarkVisibleByVeterinaries)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSRSHDateMaskInput
            name="request.executionDate"
            label={t(ETLCodes.ClosingDate)}
            readonly={true}
            visible={requestInfo?.requestStateCode === ERequestState.EXECUTED && !!data?.request?.executionDate}
          />
        </FieldGroup>

        <FieldGroup columns={2}>
          <FieldGroup columns={3}>
            <FGSRSHCheckboxInput name="isAppeal" label={t(ETLCodes.Appeal)} readonly={!editable || blocked} />
            {isAppeal ? (
              <RadioGroup
                onChange={(e) => {
                  if (+e.currentTarget.value === 0) {
                    formikRef.current.setFieldValue("appealRequestId", undefined);
                  }

                  setisAppealWithRequest(+e.currentTarget.value);
                }}
                selectedValue={isAppealWithRequest}
              >
                <Radio label={t(ETLCodes.WithRequest)} value={1} disabled={!editMode} />
                <Radio label={t(ETLCodes.WithoutRequest)} value={0} disabled={!editMode} />
              </RadioGroup>
            ) : null}

            <FGSelectInput
              name="appealRequestId"
              items={dysplasiaRequests}
              loading={drloading}
              visible={isAppeal && !!isAppealWithRequest}
              readonly={!editable || blocked}
              onItemViewClick={(item) =>
                openNewTab(`${ERoutes.dysplasiaRequest}/${appealDysplasiaRequestId}/detail/view`)
              }
            />
          </FieldGroup>
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGDogSearchInput
            name="dogId"
            label={t(ETLCodes.Dog)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
            readonly={!editable || blocked || (isAppeal && !!isAppealWithRequest) || requestInfo?.masterRequestId > 0}
            info={
              dysplasiaRequestId > 0 && (
                <SharepointButton existsInShp={dogExistsInShp} loading={dogShpLoading} onClick={openOrCreateDogInShp} />
              )
            }
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGPeopleSearchInput
            name="request.personId"
            label={t(ETLCodes.Contact)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
            readonly={!editable || blocked || (isAppeal && !!isAppealWithRequest) || requestInfo?.masterRequestId > 0}
            requiredMark
            info={<InformationTooltip tooltipMessage={ETLCodes.MustBeBelgianOwner} />}
            dogId={!isAppeal ? dogId : null}
            showWarning={true}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput
            name="commissionType"
            label={t(ETLCodes.CommissionType)}
            items={commissionTypes}
            loading={ctloading}
            readonly={!editable || blocked}
            visible={!isAppeal}
            requiredMark={!!isAppealWithRequest}
          />
        </FieldGroup>
      </FieldSet>
      <FieldSet title={t(ETLCodes.Radio)}>
        <FieldGroup columns={2}>
          <FGSRSHMultiCheckboxInput
            name="typeIdsChecked"
            items={dysplasiaTypes}
            loading={dtloading}
            columns={6}
            readonly={!editable || blocked}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSRSHDateMaskInput
            name="radioDate"
            label={t(ETLCodes.RadioDate)}
            requiredMark
            readonly={!editable || blocked || (isAppeal && !!isAppealWithRequest)}
            maxDate={new Date()}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput
            name="position"
            label={t(ETLCodes.Position)}
            items={positions}
            loading={posloading}
            readonly={!editable || blocked || (isAppeal && !!isAppealWithRequest)}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput
            name="clinicId"
            label={t(ETLCodes.Clinic)}
            items={clinics}
            loading={cloading}
            onItemViewClick={(item) => openNewTab(`${ERoutes.clinic}/${item}/detail/view`)}
            readonly={!editable || blocked || (isAppeal && !!isAppealWithRequest)}
            requiredMark={clinicIdRequired}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput
            name="vetId"
            label={t(ETLCodes.Veterinary)}
            items={clinicId ? vetsByClinic : vets}
            loading={clinicId ? vcloading : vloading}
            onItemViewClick={(item) => openPersonFromVet(item)}
            readonly={!editable || blocked || (isAppeal && !!isAppealWithRequest)}
            requiredMark={vetIdRequired}
          />
        </FieldGroup>
      </FieldSet>
      {(requestInfo?.requestStateCode === ERequestState.EVALUATED ||
        requestInfo?.requestStateCode === ERequestState.EXECUTED) && (
        <FieldSet title={t(ETLCodes.Evaluations)}>
          {data?.transientDysplasiaTypes?.map((v, index) => (
            <StyledEvaluation key={index}>
              <FieldSet title={`${t(ETLCodes.DysplasiaType)}: ${v.dysplasiaType}`}>
                {v?.transientDysplasias?.map((d, dysplasiaIndex) => (
                  <div style={{ marginLeft: "3rem" }} key={dysplasiaIndex}>
                    <FieldSet title={d.characteristicName}>
                      <FGSRSHCheckboxInput
                        name={`transientDysplasiaTypes[${index}].isRefused`}
                        label={t(ETLCodes.IsRefused)}
                        readonly
                      />
                      <DysplasiaRequestValueResult
                        index={index}
                        dysplasiaIndex={dysplasiaIndex}
                        characteristicId={d.characteristicId}
                        characteristicType={d.type}
                      />
                      <FGCustomInput label={t(ETLCodes.Remarks)}>
                        {(ctx) => (
                          <DysplasiaResultsRemarksList
                            dysplasiaRequestId={data?.dysplasiaRequestId}
                            dysplasiaTypeId={v.dysplasiaTypeId}
                            characteristicId={d.characteristicId}
                          />
                        )}
                      </FGCustomInput>
                    </FieldSet>
                  </div>
                ))}
              </FieldSet>
            </StyledEvaluation>
          ))}
        </FieldSet>
      )}

      {data?.request?.status === ERequestState.EXECUTED && (
        <InlineButtonContainer>
          <div />
          <ButtonsBloc style={{ float: "right" }}>
            <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.dysplasiaRequest}`)} />
          </ButtonsBloc>
        </InlineButtonContainer>
      )}
    </SmallFormGenerator>
  );
};
